import {
  createContext, useCallback, useState, useContext, useMemo, useEffect
} from 'react'
import { ParsedSrt, WorkWithParsedSrts } from '../pages/api/work/[id]'

export type LanguageContextType = {
  captionsPreference: string | null;
  setCaptionsPreference: (language: string) => void;
}

export const LanguageContext = createContext<LanguageContextType>({
  captionsPreference: null,
  setCaptionsPreference: () => { /* noop */ },
})

type Props = {
  children?: React.ReactNode
};
export const LanguageContextProvider: React.FC<Props> = ({ children }) => {
  const [captionsPreference, setCaptionsPreference] = useState<string>(null)

  const setCaptionsPreferenceAndCache = useCallback((captionsPref) => {
    setCaptionsPreference(captionsPref.toUpperCase())
    window.localStorage?.setItem('captionsPreference', captionsPref)
  }, [setCaptionsPreference])

  useEffect(() => {
    const initialCaptionsPreference = window.localStorage?.getItem('captionsPreference')?.toUpperCase()
    setCaptionsPreference(initialCaptionsPreference)
  }, [setCaptionsPreference])

  const provider: LanguageContextType = {
    captionsPreference,
    setCaptionsPreference: setCaptionsPreferenceAndCache,

  }

  return (
    <LanguageContext.Provider
      value={provider}
    >
      { children }
    </LanguageContext.Provider>
  )
}

export const useMultilingualCaptions = (work: WorkWithParsedSrts): (ParsedSrt | undefined | null) => {
  const { captionsPreference } = useContext(LanguageContext)

  const selectedPreference = useMemo<ParsedSrt | undefined | null>(() => {
    const matchingCaptions = work?.parsedSrts?.find(({ shortDescriptor, parsedSrt }) => shortDescriptor.toUpperCase() === captionsPreference?.toUpperCase() && parsedSrt)
    if (matchingCaptions) return matchingCaptions
    const defaultCaptions = work?.parsedSrts?.filter(({ parsedSrt }) => parsedSrt)[0]
    if (defaultCaptions?.parsedSrt) return defaultCaptions
    return null
  }, [captionsPreference, work])

  if (work?.parsedSrts?.length < 1) return null

  return selectedPreference
}
